<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { ProductQuery } from "@apps/iloom_customization/modules/products/queries/ProductQuery";
    import ReviewsItem from "@apps/iloom_customization/reviews/components/ReviewsItem.svelte";
    import { ReviewsQuery } from "@apps/iloom_customization/reviews/queries/ReviewsQuery";
    import { IloomReviewsService } from "@apps/iloom_customization/reviews/services/IloomReviewsService";
    import { GetReviewsRequestParamsDTO } from "@apps/reviews/services/ReviewsServiceBase";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import { lastValueFrom } from "rxjs";
    import { createEventDispatcher, onMount } from "svelte";

    import Slider from "@components/Slider/Slider.svelte";
    import SchemaSliderController from "@components/Slider/SliderController";
    import SliderDots from "@components/Slider/SliderDots.svelte";

    import ReviewsItemSkeleton from "./ReviewsItemSkeleton.svelte";

    export let productQuery: ProductQuery;
    export let reviewsQuery: ReviewsQuery;
    export let reviewsService: IloomReviewsService;
    export let wishlistsService: WishlistsService;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;

    const isMobile = window.innerWidth <= 600;

    const featuredReviews$ = reviewsQuery.featuredReviews$;
    const featuredStatus$ = reviewsQuery.featuredStatus$;

    const sliderController = new SchemaSliderController();
    const dispatch = createEventDispatcher();

    const reviewSkeletonItems = new Array(4)
        .fill(null)
        .map((_, idx) => idx + 1);

    const getFeaturedReviews = () => {
        const params: GetReviewsRequestParamsDTO = {
            perPage: 4,
            sortingOptions: ["mostRecent" as const],
            onlyProductReviews: false,
            onlyShopReviews: false,
            onlyPinnedReviews: true,
            onlyWithPhotos: true,
        };
        lastValueFrom(reviewsService.getFeaturedReviews(params));
    };

    onMount(() => {
        getFeaturedReviews();
    });

    const currentIndex$ = sliderController.slideIndex$;

    const handleOpenReviewModal = (
        event: CustomEvent<{ reviewId: number }>
    ) => {
        dispatch("openModal", {
            reviewId: event.detail.reviewId,
            reviews$: featuredReviews$,
        });
    };
</script>

{#if $featuredStatus$ === STATUS.LOADING}
    {#if isMobile}
        <ReviewsItemSkeleton />
    {:else}
        <div class="gw-crv-featured-list">
            {#each reviewSkeletonItems as key (key)}
                <ReviewsItemSkeleton />
            {/each}
        </div>
    {/if}
{/if}

{#if $featuredStatus$ === STATUS.READY}
    {#if $featuredReviews$.length}
        <div class="gw-crv-featured-list-spacer" />
        {#if isMobile}
            <Slider {sliderController} perPage={1} loop={false}>
                <svelte:fragment slot="slides">
                    {#each $featuredReviews$ as review}
                        <ReviewsItem
                            {productQuery}
                            {review}
                            {wishlistProductStatusesQuery}
                            {wishlistsService}
                            isFeatured={true}
                            on:openModal={handleOpenReviewModal}
                        />
                    {/each}
                </svelte:fragment>
                <SliderDots
                    {sliderController}
                    slot="dots"
                    perPage={1}
                    currentIndex={$currentIndex$ || 0}
                    slidesCount={$featuredReviews$.length}
                    class="gw-crv-dots"
                />
            </Slider>
        {:else}
            <div class="gw-crv-featured-list">
                {#each $featuredReviews$ as review}
                    <ReviewsItem
                        {productQuery}
                        {review}
                        {wishlistProductStatusesQuery}
                        {wishlistsService}
                        isFeatured={true}
                        on:openModal={handleOpenReviewModal}
                    />
                {/each}
            </div>
        {/if}
    {/if}
{/if}

<style lang="postcss">
    .gw-crv-featured-list-spacer {
        margin-bottom: 80px;
        @media (max-width: 600px) {
            margin-bottom: 24px;
        }
    }
    .gw-crv-featured-list {
        display: grid;
        align-items: start;
        grid-template-columns: repeat(4, 1fr);
        gap: 30px;
    }
    :global(.gw-dots.gw-crv-dots) {
        column-gap: 0;
        list-style: none;
        margin: 0;
        padding: 24px 0 0;
    }
    :global(.gw-dots.gw-crv-dots .gw-dots__dot) {
        display: flex;
        padding: 4px;
    }
    :global(.gw-dots.gw-crv-dots .gw-dots__button) {
        width: 8px;
        height: 8px;
        border: 1px solid #bebebe;
        border-radius: 50%;
    }
    :global(.gw-dots.gw-crv-dots .gw-dots__button--active) {
        border-color: #666666;
        background-color: #666666;
    }
</style>
